// TODO: deprecated — only consumed by RemotionTimeline (itself deprecated).
// Safe to delete together with the legacy timeline cleanup.
import React from "react";
import PropTypes from "prop-types";
import { Row, Col, FormGroup, FormLabel, FormControl } from "react-bootstrap";
import { motion } from "framer-motion";
import i18n from "i18next";
import AppModal from "../../common/modal/AppModal";

const BOX_ANIMATION_PRESETS = [
  {
    name: "Fade",
    label: "animation_fade",
    animation: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      transition: { duration: 1 },
    },
  },
  {
    name: "Flip",
    label: "animation_flip",
    animation: {
      initial: { rotateY: -90, opacity: 0 },
      animate: { rotateY: 0, opacity: 1 },
      transition: { duration: 1 },
    },
  },
  {
    name: "Pinwheel",
    label: "animation_pinwheel",
    animation: {
      initial: { rotate: -180, scale: 0, opacity: 0 },
      animate: { rotate: 0, scale: 1, opacity: 1 },
      transition: { duration: 1 },
    },
  },
  {
    name: "Roll",
    label: "animation_roll",
    animation: {
      initial: { x: -100, rotate: -180, opacity: 0 },
      animate: { x: 0, rotate: 0, opacity: 1 },
      transition: { duration: 1 },
    },
  },
  {
    name: "Rotate",
    label: "animation_rotate",
    animation: {
      initial: { rotate: -180, opacity: 0 },
      animate: { rotate: 0, opacity: 1 },
      transition: { duration: 1 },
    },
  },
  {
    name: "Stretch",
    label: "animation_stretch",
    animation: {
      initial: { scaleY: 0, opacity: 0 },
      animate: { scaleY: 1, opacity: 1 },
      transition: { duration: 1 },
    },
  },
  {
    name: "Zoom",
    label: "animation_zoom",
    animation: {
      initial: { scale: 0, opacity: 0 },
      animate: { scale: 1, opacity: 1 },
      transition: { duration: 1 },
    },
  },
];

const BoxAnimationModal = ({
  visible,
  onClose,
  onApply,
  onDelete,
  initialAnimation,
  initialDuration,
  initialDelay,
}) => {
  const [selectedAnimation, setSelectedAnimation] = React.useState(() => {
    if (initialAnimation) {
      const foundPreset = BOX_ANIMATION_PRESETS.find(
        (preset) => preset.name === initialAnimation.name
      );
      return foundPreset || BOX_ANIMATION_PRESETS[0];
    }
    return BOX_ANIMATION_PRESETS[0];
  });
  const [duration, setDuration] = React.useState(initialDuration || 1);
  const [delay, setDelay] = React.useState(initialDelay || 0);
  const [previewKey, setPreviewKey] = React.useState(0);

  const toggleModal = React.useCallback(
    (e) => {
      let key = e.keyCode ? e.keyCode : e.which;
      if (key === 27 && visible) {
        onClose();
      }
    },
    [visible, onClose]
  );

  const onAnimationSelect = (animation) => {
    setSelectedAnimation(animation);
    setPreviewKey((prev) => prev + 1);
  };

  const onDurationChange = (newDuration) => {
    if (newDuration === "") {
      setDuration("");
      const updatedAnimation = {
        ...selectedAnimation,
        animation: {
          ...selectedAnimation.animation,
          transition: { ...selectedAnimation.animation.transition, duration: 0.1 },
        },
      };
      setSelectedAnimation(updatedAnimation);
      setPreviewKey((prev) => prev + 1);
      return;
    }
    const parsedDuration = parseFloat(newDuration);
    if (isNaN(parsedDuration)) return;

    const clampedDuration = Math.min(Math.max(parsedDuration, 0.1), 10);

    const updatedAnimation = {
      ...selectedAnimation,
      animation: {
        ...selectedAnimation.animation,
        transition: {
          ...selectedAnimation.animation.transition,
          duration: clampedDuration,
        },
      },
    };
    setSelectedAnimation(updatedAnimation);
    setDuration(newDuration);
    setPreviewKey((prev) => prev + 1);
  };

  const onDelayChange = (newDelay) => {
    if (newDelay === "") {
      setDelay("");
      const updatedAnimation = {
        ...selectedAnimation,
        animation: {
          ...selectedAnimation.animation,
          transition: { ...selectedAnimation.animation.transition, delay: 0 },
        },
      };
      setSelectedAnimation(updatedAnimation);
      setPreviewKey((prev) => prev + 1);
      return;
    }
    const parsedDelay = parseFloat(newDelay);
    if (isNaN(parsedDelay)) return;

    const clampedDelay = Math.max(parsedDelay, 0);

    const updatedAnimation = {
      ...selectedAnimation,
      animation: {
        ...selectedAnimation.animation,
        transition: {
          ...selectedAnimation.animation.transition,
          delay: clampedDelay,
        },
      },
    };
    setSelectedAnimation(updatedAnimation);
    setDelay(newDelay);
    setPreviewKey((prev) => prev + 1);
  };

  const playPreview = () => {
    setPreviewKey((prev) => prev + 1);
  };

  React.useEffect(() => {
    window.addEventListener("keyup", toggleModal);
    return () => {
      window.removeEventListener("keyup", toggleModal);
    };
  }, [toggleModal]);

  React.useEffect(() => {
    if (visible) {
      if (initialAnimation) {
        const foundPreset = BOX_ANIMATION_PRESETS.find(
          (preset) => preset.name === initialAnimation.name
        );
        if (foundPreset) {
          setSelectedAnimation(foundPreset);
        } else {
          setSelectedAnimation(BOX_ANIMATION_PRESETS[0]);
        }
      } else {
        setSelectedAnimation(BOX_ANIMATION_PRESETS[0]);
      }

      setDuration(initialDuration || 1);
      setDelay(initialDelay || 0);
      setPreviewKey((prev) => prev + 1);
    }
  }, [visible, initialAnimation, initialDuration, initialDelay]);

  const footer = (
    <div className="app-modal__footer" style={{ justifyContent: "space-between" }}>
      <div>
        {onDelete && (
          <button
            type="button"
            className="app-modal__btn app-modal__btn--danger"
            onClick={() => {
              onDelete();
              onClose();
            }}
          >
            {i18n.t("BoxAnimations.deleteAnimation") || "Delete Animation"}
          </button>
        )}
      </div>
      <div style={{ display: "flex", gap: "12px" }}>
        <button type="button" className="app-modal__btn app-modal__btn--ghost" onClick={onClose}>
          {i18n.t("Cancel")}
        </button>
        <button
          type="button"
          className="app-modal__btn app-modal__btn--primary"
          onClick={() => {
            const _duration =
              duration === "" ? 0.1 : Math.min(Math.max(parseFloat(duration) || 0.1, 0.1), 10);
            const _delay = delay === "" ? 0 : Math.max(parseFloat(delay) || 0, 0);
            onApply(selectedAnimation, _duration, _delay);
            onClose();
          }}
        >
          {i18n.t("BoxAnimations.applyAnimation") || "Apply Animation"}
        </button>
      </div>
    </div>
  );

  return (
    <AppModal
      className="textAnimationModal"
      backdrop
      show={visible}
      onClose={onClose}
      title={i18n.t("BoxAnimations--title") || "Box --Animations"}
      dialogClassName="text-animation-modal-dialog"
      size="xl"
      footer={footer}
    >
      <Row>
        <Col md={4}>
          <h5>{i18n.t("BoxAnimations.chooseAnimation") || "Choose Animation"}</h5>
          <div className="animation-list" style={{ maxHeight: "400px", overflowY: "auto" }}>
            {BOX_ANIMATION_PRESETS.map((preset, index) => (
              <div
                key={index}
                className={`animation-item ${
                  selectedAnimation.name === preset.name ? "selected" : ""
                }`}
                onClick={() => onAnimationSelect(preset)}
              >
                <strong>{i18n.t(preset.label) || preset.name}</strong>
              </div>
            ))}
          </div>
        </Col>

        <Col md={8}>
          <Row>
            <Col md={8}>
              <h5>{i18n.t("BoxAnimations.preview") || "Preview"}</h5>
              <div
                className="preview-area"
                style={{
                  height: "300px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  overflow: "hidden",
                  backgroundColor: "#f8f9fa",
                  border: "2px dashed #dee2e6",
                }}
              >
                <motion.div
                  key={previewKey}
                  initial={selectedAnimation.animation.initial}
                  animate={selectedAnimation.animation.animate}
                  transition={{
                    ...selectedAnimation.animation.transition,
                    delay: delay === "" ? 0 : parseFloat(delay) || 0,
                  }}
                  style={{
                    width: "120px",
                    height: "80px",
                    backgroundColor: "#007bff",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "14px",
                  }}
                >
                  {i18n.t("BoxAnimations.sampleText") || "Sample Box"}
                </motion.div>
              </div>
              <div style={{ marginTop: "10px", textAlign: "center" }}>
                <button
                  type="button"
                  className="app-modal__btn app-modal__btn--ghost"
                  onClick={playPreview}
                  style={{ marginRight: "10px" }}
                >
                  <i className="material-icons">replay</i>{" "}
                  {i18n.t("BoxAnimations.replay") || "Replay"}
                </button>
              </div>
            </Col>

            <Col md={4}>
              <div className="settings-panel">
                <h5>{i18n.t("BoxAnimations.settings") || "Settings"}</h5>
                <FormGroup>
                  <FormLabel>{i18n.t("BoxAnimations.duration") || "Duration (seconds)"}</FormLabel>
                  <FormControl
                    type="number"
                    min="0.1"
                    max="10"
                    step="0.1"
                    value={duration}
                    onChange={(e) => onDurationChange(e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel>{i18n.t("BoxAnimations.delay") || "Delay (seconds)"}</FormLabel>
                  <FormControl
                    type="number"
                    min="0"
                    max="999"
                    step="0.1"
                    value={delay}
                    onChange={(e) => onDelayChange(e.target.value)}
                  />
                  <small className="text-muted">
                    {i18n.t("BoxAnimations.delayDescription") || "Delay before animation starts"}
                  </small>
                </FormGroup>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </AppModal>
  );
};

BoxAnimationModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  initialAnimation: PropTypes.object,
  initialDuration: PropTypes.number,
  initialDelay: PropTypes.number,
};

export default BoxAnimationModal;
